import React, { useRef, useState } from 'react'
import emailjs from '@emailjs/browser'
import Seo from './Seo'
import { blackButtonSurfaceClass, buttonFocusRingClass } from '../shared/buttonStyles'

const inputClassName = 'w-full appearance-none rounded-lg border border-zinc-700 bg-[#131316] px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] focus:border-cyan-700/70 focus:outline-none focus:ring-1 focus:ring-cyan-700/45'
const fieldLabelClassName = 'mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-300'
const emailJsConfig = {
    serviceId: process.env.REACT_APP_WRGG_EMAILJS_SERVICE_ID,
    templateId: process.env.REACT_APP_WRGG_EMAILJS_TEMPLATE_ID,
    publicKey: process.env.REACT_APP_WRGG_EMAILJS_PUBLIC_KEY,
}
const isContactFormConfigured = Object.values(emailJsConfig).every(Boolean)

const Contact = () => {
    const form = useRef(null)
    const [message, setMessage] = useState({ text: '', type: '' })

    const sendEmail = (event) => {
        event.preventDefault()

        if (!isContactFormConfigured) {
            setMessage({ text: 'The contact form is temporarily unavailable. Please try again later.', type: 'error' })
            return
        }

        emailjs.sendForm(
            emailJsConfig.serviceId,
            emailJsConfig.templateId,
            form.current,
            {
                publicKey: emailJsConfig.publicKey,
            },
        ).then(
            () => {
                setMessage({ text: 'Thank you. Your message was sent successfully!', type: 'success' })
                form.current.reset()
            },
            () => {
                setMessage({ text: 'Failed to send message. Please try again later.', type: 'error' })
            },
        )
    }

    function closePopup() {
        setMessage({ text: '', type: '' })
    }

    return (
        <div className="mx-auto w-full max-w-screen-lg px-4 pt-6 pb-4 text-zinc-200 custom-lg:px-0 sm:pt-8">
            <Seo
                title="Contact WINRATES.GG™ | Feedback, Bugs, and Support"
                description="Contact WINRATES.GG™ to report bugs, send feedback, or ask questions about the unified League of Legends® companion app."
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'ContactPage',
                    name: 'Contact WINRATES.GG',
                    url: 'https://winrates.gg/contact',
                    description: 'Feedback, bug reports, and support for WINRATES.GG.',
                }}
            />
            {message.text ? (
                <div className="fixed inset-x-4 top-20 z-50 flex justify-center sm:inset-x-0">
                    <div
                        className={`flex max-w-[520px] items-start gap-3 rounded-xl border px-4 py-3 shadow-[0_20px_40px_-28px_rgba(0,0,0,0.95)] ${
                            message.type === 'success'
                                ? 'border-cyan-700/45 bg-zinc-900 text-cyan-100'
                                : 'border-rose-900/45 bg-zinc-900 text-rose-100'
                        }`}
                    >
                        <div className="min-w-0 flex-1 text-sm font-medium leading-6">{message.text}</div>
                        <button
                            aria-label="Close message"
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-white/10 bg-zinc-950/70 text-base leading-none transition-colors hover:bg-zinc-900/80"
                            onClick={closePopup}
                            type="button"
                        >
                            ×
                        </button>
                    </div>
                </div>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_300px]">
                <section className="rounded-xl border border-zinc-700 bg-zinc-950/78 p-5 shadow-[0_18px_36px_-28px_rgba(0,0,0,0.95)] sm:p-6">
                    <div className="mb-5 border-b border-zinc-800 pb-4">
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-300">Contact</div>
                        <h1 className="mt-2 text-xl font-semibold tracking-tight text-zinc-100 sm:text-2xl">Send a note</h1>
                        <p className="mt-2 max-w-[62ch] text-sm leading-6 text-zinc-300">
                            WINRATES.GG™ is currently in open beta. Have a suggestion, noticed a bug, or want to send feedback?
                            Use the form below. If you are reporting an issue, include as much detail as you can.
                        </p>
                        <p className="mt-2 max-w-[62ch] text-sm leading-6 text-zinc-300">
                            You can also reach me on{' '}
                            <a
                                className="font-semibold text-cyan-300 underline decoration-cyan-300/40 underline-offset-4 transition-colors hover:text-cyan-200"
                                href="https://www.linkedin.com/in/victor-haynes/"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                LinkedIn
                            </a>
                            .
                        </p>
                    </div>

                    <form className="space-y-4" onSubmit={sendEmail} ref={form}>
                        <div>
                            <label className={fieldLabelClassName} htmlFor="contact-email">
                                Email
                            </label>
                            <input
                                className={inputClassName}
                                id="contact-email"
                                name="user_email"
                                placeholder="name@example.com"
                                type="email"
                            />
                        </div>

                        <div>
                            <label className={fieldLabelClassName} htmlFor="contact-message">
                                Message
                            </label>
                            <textarea
                                className={`${inputClassName} min-h-[180px] resize-y`}
                                id="contact-message"
                                name="message"
                                placeholder="Tell me what happened, what you expected, and anything else that would help."
                            />
                        </div>

                        <div className="flex items-center justify-between gap-3 border-t border-zinc-800 pt-4">
                            <div className="text-[11px] leading-5 text-zinc-300">
                                Responses are not guaranteed, but there is a very high chance I will write back to you.
                            </div>
                            <button
                                className={`inline-flex h-10 items-center justify-center rounded-md px-4 text-[12px] font-semibold uppercase tracking-[0.18em] transition-colors ${buttonFocusRingClass} ${blackButtonSurfaceClass}`}
                                type="submit"
                            >
                                Send
                            </button>
                        </div>
                    </form>
                </section>

                <aside className="space-y-4">
                    <section className="rounded-xl border border-zinc-700 bg-zinc-950/78 p-5 shadow-[0_18px_36px_-28px_rgba(0,0,0,0.95)]">
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-300">Support</div>
                        <div className="mt-2 text-lg font-semibold tracking-tight text-zinc-100">One-man project</div>
                        <p className="mt-2 text-sm leading-6 text-zinc-300">
                            WR.GG is a one-man show that incurs real costs. If the app is useful and you want to help cover hosting costs, you can leave a tip or support the project on Patreon.
                        </p>

                        <div className="mt-4 flex flex-wrap gap-2">
                            <a
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-yellow-400 px-4 text-sm font-semibold text-sky-950 transition-transform hover:scale-[1.01] hover:bg-yellow-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-200/80"
                                href="https://www.paypal.com/donate/?hosted_button_id=W2DWJWBRVBEV8"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                <span>Tip with</span>
                                <img
                                    alt="PayPal"
                                    className="h-4 w-auto"
                                    src={`${process.env.PUBLIC_URL}/assets/paypal_logo.png`}
                                />
                            </a>
                            <a
                                className="inline-flex h-10 items-center justify-center rounded-full border border-[#ff424d]/60 bg-[#ff424d]/10 px-4 text-sm font-semibold text-rose-100 transition-colors hover:border-[#ff424d] hover:bg-[#ff424d]/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff7a80]/80"
                                href="https://www.patreon.com/VictorHaynesDevelopment"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Support on Patreon
                            </a>
                        </div>

                    </section>

                    <section className="rounded-xl border border-zinc-700 bg-zinc-950/78 p-5 shadow-[0_18px_36px_-28px_rgba(0,0,0,0.95)]">
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-300">Thank You</div>
                        <p className="mt-2 max-w-[62ch] text-sm leading-6 text-zinc-300">
                            The more people use WINRATES.GG™ the higher throughput Riot Games provides for the API, which means faster load times and better features for everyone—for free. Thank you for using the app, and thank you in advance if you choose to send a tip. It all helps a lot.
                        </p>
                    </section>
                </aside>
            </div>
        </div>
    )
}

export default Contact
